import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  useListRegSpSoftwareQuery,
  useGetRegSpSoftwareQuery,
  useGetRegSpAuditQuery,
  useCreateRegSpSoftwareMutation,
  useUpdateRegSpSoftwareMutation,
  useDeleteRegSpSoftwareMutation,
  useSignoffRegSpSoftwareMutation,
  useAddRegSpLinkMutation,
  useDeleteRegSpLinkMutation,
  useUploadRegSpDocumentMutation,
  useGetRegSpDocumentUrlMutation,
  useDeleteRegSpDocumentMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#b00020',
};

// The three due-diligence sections. Each maps to a notes column and a
// sign-off (approver username + timestamp) column on the software record.
const SECTIONS = [
  {
    key: 'fintech',
    title: 'Fintech Due Diligence',
    blurb: 'Functionality, cost, admin controls, and overall fit vs. alternatives.',
    icon: 'graph-up-arrow',
    notesField: 'fintech_notes',
    byField: 'fintech_approved_by',
    atField: 'fintech_approved_at',
  },
  {
    key: 'it',
    title: 'IT / Cybersecurity Review',
    blurb: 'Security posture and data handling — is our clients’ PII protected?',
    icon: 'shield-lock',
    notesField: 'it_notes',
    byField: 'it_approved_by',
    atField: 'it_approved_at',
  },
  {
    key: 'compliance',
    title: 'Compliance Review',
    blurb: 'Does the Fintech and IT due diligence meet regulatory standards?',
    icon: 'clipboard-check',
    notesField: 'compliance_notes',
    byField: 'compliance_approved_by',
    atField: 'compliance_approved_at',
  },
];

const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return s; }
};

const card = {
  background: 'white',
  border: `1px solid ${brand.softGray}`,
  borderRadius: 10,
  padding: '1rem 1.15rem',
  marginBottom: '1rem',
};

const label = { fontWeight: 700, color: brand.deepBlue, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' };

const textInput = {
  width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 6,
  padding: '0.5rem 0.65rem', fontSize: '0.9rem', color: brand.charcoal,
};

function Btn({ children, onClick, disabled, variant = 'primary', size = 'md', type = 'button', title }) {
  const palette = {
    primary:  { bg: brand.deepBlue,  color: 'white' },
    teal:     { bg: brand.tealBlue,  color: 'white' },
    gold:     { bg: brand.goldenrod, color: brand.deepBlue },
    green:    { bg: brand.green,     color: 'white' },
    danger:   { bg: 'transparent',   color: brand.red, border: `1px solid ${brand.red}` },
    ghost:    { bg: 'transparent',   color: brand.deepBlue, border: `1px solid ${brand.softGray}` },
  }[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        background: palette.bg, color: palette.color, border: palette.border || 0,
        borderRadius: 6, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
        padding: size === 'sm' ? '0.35rem 0.7rem' : '0.55rem 1.1rem',
        fontSize: size === 'sm' ? '0.8rem' : '0.9rem',
      }}
    >{children}</button>
  );
}
Btn.propTypes = {
  children: PropTypes.node, onClick: PropTypes.func, disabled: PropTypes.bool,
  variant: PropTypes.string, size: PropTypes.string, type: PropTypes.string, title: PropTypes.string,
};

// ── One due-diligence section (notes + sign-off) ────────────────────────────
function SectionCard({ section, record, draftNotes, onNotesChange, onSignoff, onSave, dirty, saving, busy }) {
  const approvedBy = record[section.byField];
  const approvedAt = record[section.atField];
  const signed = !!approvedBy;
  return (
    <div style={{ ...card, borderTop: `3px solid ${signed ? brand.green : brand.goldenrod}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <i className={`bi bi-${section.icon}`} style={{ color: brand.deepBlue }} />
        <span style={{ fontWeight: 800, color: brand.deepBlue, fontSize: '1rem' }}>{section.title}</span>
        {signed && (
          <span style={{ marginLeft: 'auto', background: 'rgba(26,127,78,0.12)', color: brand.green, fontWeight: 700, fontSize: '0.72rem', padding: '0.2rem 0.55rem', borderRadius: 999 }}>
            <i className="bi bi-check-circle-fill me-1" />Signed off
          </span>
        )}
      </div>
      <div style={{ color: brand.charcoal, fontSize: '0.82rem', marginBottom: '0.6rem' }}>{section.blurb}</div>
      <textarea
        rows={5}
        value={draftNotes}
        onChange={(e) => onNotesChange(section.notesField, e.target.value)}
        placeholder={`Record the ${section.title.toLowerCase()} findings here…`}
        style={{ ...textInput, resize: 'vertical', fontFamily: 'inherit' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
        <Btn variant="primary" size="sm" disabled={!dirty || saving} onClick={onSave}>
          {saving ? 'Saving…' : 'Save changes'}
        </Btn>
        {dirty && <span style={{ color: brand.goldenrod, fontSize: '0.8rem' }}><i className="bi bi-exclamation-circle me-1" />Unsaved changes</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {signed ? (
            <>
              <span style={{ color: brand.charcoal, fontSize: '0.82rem' }}>
                <i className="bi bi-person-check me-1" style={{ color: brand.green }} />
                Approved by <strong style={{ color: brand.deepBlue }}>{approvedBy}</strong>
                {approvedAt && <> on {fmtDate(approvedAt)}</>}
              </span>
              <Btn variant="ghost" size="sm" disabled={busy} onClick={() => onSignoff(section.key, false)}>
                Revoke sign-off
              </Btn>
            </>
          ) : (
            <Btn variant="green" size="sm" disabled={busy} onClick={() => onSignoff(section.key, true)}>
              <i className="bi bi-check2-circle me-1" />Sign off as complete
            </Btn>
          )}
        </span>
      </div>
    </div>
  );
}
SectionCard.propTypes = {
  section: PropTypes.object.isRequired,
  record: PropTypes.object.isRequired,
  draftNotes: PropTypes.string,
  onNotesChange: PropTypes.func.isRequired,
  onSignoff: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  dirty: PropTypes.bool,
  saving: PropTypes.bool,
  busy: PropTypes.bool,
};

// ── Detail pane for the selected software record ────────────────────────────
function SoftwareDetail({ id, onDeleted }) {
  const { data: record, isFetching, refetch } = useGetRegSpSoftwareQuery({ id }, { skip: !id });
  const { data: auditData, refetch: refetchAudit } = useGetRegSpAuditQuery({ id }, { skip: !id });

  const [updateSoftware, { isLoading: isSaving }] = useUpdateRegSpSoftwareMutation();
  const [deleteSoftware, { isLoading: isDeleting }] = useDeleteRegSpSoftwareMutation();
  const [signoff, { isLoading: isSigning }] = useSignoffRegSpSoftwareMutation();
  const [addLink, { isLoading: isAddingLink }] = useAddRegSpLinkMutation();
  const [deleteLink] = useDeleteRegSpLinkMutation();
  const [uploadDoc, { isLoading: isUploading }] = useUploadRegSpDocumentMutation();
  const [getDocUrl] = useGetRegSpDocumentUrlMutation();
  const [deleteDoc] = useDeleteRegSpDocumentMutation();

  // Editable fields are seeded from the record and saved together.
  const [draft, setDraft] = useState({});
  const [error, setError] = useState(null);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    if (record) {
      setDraft({
        name: record.name || '',
        category: record.category || '',
        description: record.description || '',
        fintech_notes: record.fintech_notes || '',
        it_notes: record.it_notes || '',
        compliance_notes: record.compliance_notes || '',
      });
    }
    // Intentionally keyed on record.id only: re-seed the editable draft when a
    // different record loads, not on every field change to the same record.
  }, [record?.id]);

  // Whether any of the given fields differ from the saved record. Each card
  // uses it to enable/disable its own Save button.
  const isDirty = (keys) => !!record && keys.some(k => (draft[k] || '') !== (record[k] || ''));

  const dirty = useMemo(() => {
    if (!record) return false;
    return ['name', 'category', 'description', 'fintech_notes', 'it_notes', 'compliance_notes']
      .some(k => (draft[k] || '') !== (record[k] || ''));
  }, [draft, record]);

  if (!id) {
    return (
      <div style={{ ...card, color: brand.charcoal, textAlign: 'center', padding: '3rem 1rem' }}>
        Select a software record on the left, or add a new one.
      </div>
    );
  }
  if (isFetching && !record) return <div style={card}>Loading…</div>;
  if (!record) return <div style={card}>Not found.</div>;

  const onField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setError(null);
    if (!(draft.name || '').trim()) { setError('A software name is required.'); return; }
    try {
      await updateSoftware({ id, body: draft }).unwrap();
      refetch(); refetchAudit();
    } catch (e) { setError(e?.data?.detail || 'Could not save changes.'); }
  };

  const handleSignoff = async (section, approved) => {
    setError(null);
    try {
      // Persist any pending notes edits first so a sign-off never certifies
      // notes that were only in the textarea.
      if (dirty) await updateSoftware({ id, body: draft }).unwrap();
      await signoff({ id, section, approved }).unwrap();
      refetch(); refetchAudit();
    } catch (e) { setError(e?.data?.detail || 'Could not update the sign-off.'); }
  };

  const handleAddLink = async () => {
    setError(null);
    if (!linkUrl.trim()) { setError('A URL is required.'); return; }
    try {
      await addLink({ id, label: linkLabel.trim() || null, url: linkUrl.trim() }).unwrap();
      setLinkLabel(''); setLinkUrl(''); refetch(); refetchAudit();
    } catch (e) { setError(e?.data?.detail || 'Could not add the link.'); }
  };

  const handleDeleteLink = async (linkId) => {
    try { await deleteLink({ id, linkId }).unwrap(); refetch(); refetchAudit(); }
    catch (e) { setError(e?.data?.detail || 'Could not remove the link.'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    const body = new FormData();
    body.append('file', file);
    try { await uploadDoc({ id, body }).unwrap(); refetch(); refetchAudit(); }
    catch (err) { setError(err?.data?.detail || 'Upload failed.'); }
  };

  const handleDownload = async (docId) => {
    try {
      const { url } = await getDocUrl({ docId }).unwrap();
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) { setError(e?.data?.detail || 'Could not open the document.'); }
  };

  const handleDeleteDoc = async (docId) => {
    try { await deleteDoc({ docId }).unwrap(); refetch(); refetchAudit(); }
    catch (e) { setError(e?.data?.detail || 'Could not remove the document.'); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete “${record.name}” and all of its due-diligence, links, and documents? This cannot be undone.`)) return;
    try { await deleteSoftware({ id }).unwrap(); onDeleted(); }
    catch (e) { setError(e?.data?.detail || 'Could not delete this record.'); }
  };

  const audit = auditData?.audit || [];

  return (
    <div>
      {error && <div style={{ ...card, borderColor: brand.red, color: brand.red }}>{error}</div>}

      {/* Overview */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <div style={{ fontWeight: 800, color: brand.deepBlue, fontSize: '1.15rem' }}>Software details</div>
          <Btn variant="danger" size="sm" disabled={isDeleting} onClick={handleDelete}>
            <i className="bi bi-trash me-1" />Delete record
          </Btn>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <div style={label}>Name</div>
            <input style={textInput} value={draft.name || ''} onChange={(e) => onField('name', e.target.value)} />
          </div>
          <div>
            <div style={label}>Category</div>
            <input style={textInput} value={draft.category || ''} onChange={(e) => onField('category', e.target.value)}
              placeholder="AI, password manager, note taker…" />
          </div>
        </div>
        <div>
          <div style={label}>What it’s used for</div>
          <textarea rows={3} style={{ ...textInput, resize: 'vertical', fontFamily: 'inherit' }}
            value={draft.description || ''} onChange={(e) => onField('description', e.target.value)}
            placeholder="Describe how this software is used at the firm." />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
          <Btn variant="primary" size="sm" disabled={!isDirty(['name', 'category', 'description']) || isSaving} onClick={handleSave}>
            {isSaving ? 'Saving…' : 'Save changes'}
          </Btn>
          {isDirty(['name', 'category', 'description']) && <span style={{ color: brand.goldenrod, fontSize: '0.82rem' }}><i className="bi bi-exclamation-circle me-1" />Unsaved changes</span>}
        </div>
      </div>

      {/* Compliance links */}
      <div style={card}>
        <div style={{ ...label, marginBottom: '0.5rem' }}>Compliance links</div>
        {(record.links || []).length === 0 && <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginBottom: '0.6rem' }}>No links yet.</div>}
        {(record.links || []).map(lnk => (
          <div key={lnk.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', borderBottom: `1px solid ${brand.softGray}` }}>
            <i className="bi bi-link-45deg" style={{ color: brand.tealBlue }} />
            <a href={lnk.url} target="_blank" rel="noreferrer" style={{ color: brand.tealBlue, fontWeight: 600, wordBreak: 'break-all' }}>
              {lnk.label || lnk.url}
            </a>
            <button type="button" onClick={() => handleDeleteLink(lnk.id)} title="Remove link"
              style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: brand.red, cursor: 'pointer' }}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem', flexWrap: 'wrap' }}>
          <input style={{ ...textInput, flex: '1 1 140px' }} placeholder="Label (optional)" value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} />
          <input style={{ ...textInput, flex: '2 1 240px' }} placeholder="https://…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
          <Btn variant="teal" size="sm" disabled={isAddingLink} onClick={handleAddLink}><i className="bi bi-plus-lg me-1" />Add link</Btn>
        </div>
      </div>

      {/* Compliance documents */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          <div style={label}>Compliance documents</div>
          <label style={{ marginLeft: 'auto', cursor: 'pointer', background: brand.tealBlue, color: 'white', fontWeight: 700, fontSize: '0.8rem', padding: '0.35rem 0.7rem', borderRadius: 6 }}>
            <i className="bi bi-upload me-1" />{isUploading ? 'Uploading…' : 'Upload document'}
            <input type="file" hidden onChange={handleUpload} disabled={isUploading} />
          </label>
        </div>
        {(record.documents || []).length === 0 && <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>No documents uploaded.</div>}
        {(record.documents || []).map(doc => (
          <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', borderBottom: `1px solid ${brand.softGray}` }}>
            <i className="bi bi-file-earmark-text" style={{ color: brand.deepBlue }} />
            <button type="button" onClick={() => handleDownload(doc.id)}
              style={{ background: 'transparent', border: 0, color: brand.tealBlue, fontWeight: 600, cursor: 'pointer', textAlign: 'left', wordBreak: 'break-all' }}>
              {doc.filename || 'document'}
            </button>
            <span style={{ color: brand.charcoal, fontSize: '0.75rem' }}>
              {doc.uploaded_by ? `· ${doc.uploaded_by}` : ''} {doc.uploaded ? `· ${fmtDate(doc.uploaded)}` : ''}
            </span>
            <button type="button" onClick={() => handleDeleteDoc(doc.id)} title="Remove document"
              style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: brand.red, cursor: 'pointer' }}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
        ))}
      </div>

      {/* Three due-diligence sections */}
      {SECTIONS.map(section => (
        <SectionCard
          key={section.key}
          section={section}
          record={record}
          draftNotes={draft[section.notesField] || ''}
          onNotesChange={onField}
          onSignoff={handleSignoff}
          onSave={handleSave}
          dirty={isDirty([section.notesField])}
          saving={isSaving}
          busy={isSigning || isSaving}
        />
      ))}

      {/* Audit trail */}
      <div style={card}>
        <button type="button" onClick={() => setShowAudit(s => !s)}
          style={{ background: 'transparent', border: 0, cursor: 'pointer', ...label, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <i className={`bi bi-chevron-${showAudit ? 'down' : 'right'}`} />
          Change history ({audit.length})
        </button>
        {showAudit && (
          <div style={{ marginTop: '0.6rem' }}>
            {audit.length === 0 && <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>No history yet.</div>}
            {audit.map(a => (
              <div key={a.id} style={{ display: 'flex', gap: '0.6rem', padding: '0.3rem 0', borderBottom: `1px solid ${brand.softGray}`, fontSize: '0.82rem' }}>
                <span style={{ color: brand.charcoal, whiteSpace: 'nowrap' }}>{fmtDate(a.created)}</span>
                <span style={{ color: brand.deepBlue, fontWeight: 700, whiteSpace: 'nowrap' }}>{a.actor || '—'}</span>
                <span style={{ color: brand.charcoal }}>{a.detail || a.action}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
SoftwareDetail.propTypes = { id: PropTypes.number, onDeleted: PropTypes.func.isRequired };

// ── Page ─────────────────────────────────────────────────────────────────────
export default function RegSP() {
  const { data, isLoading, refetch } = useListRegSpSoftwareQuery();
  const [createSoftware, { isLoading: isCreating }] = useCreateRegSpSoftwareMutation();

  const software = data?.software || [];
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [error, setError] = useState(null);

  const signoffCount = (s) =>
    [s.fintech_approved_by, s.it_approved_by, s.compliance_approved_by].filter(Boolean).length;

  const handleCreate = async () => {
    setError(null);
    if (!newName.trim()) { setError('A software name is required.'); return; }
    try {
      const created = await createSoftware({ body: { name: newName.trim(), category: newCategory.trim() || null } }).unwrap();
      setNewName(''); setNewCategory(''); setShowCreate(false);
      await refetch();
      if (created?.id) setSelectedId(created.id);
    } catch (e) { setError(e?.data?.detail || 'Could not create the record.'); }
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
        <h3 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>
          <i className="bi bi-shield-lock me-2" style={{ color: brand.goldenrod }} />Reg S-P Software Register
        </h3>
        <Btn variant="gold" onClick={() => setShowCreate(s => !s)}>
          <i className="bi bi-plus-lg me-1" />Add software
        </Btn>
      </div>
      <p style={{ color: brand.charcoal, fontSize: '0.9rem', maxWidth: 780 }}>
        Vendor due diligence for safeguarding client information. Fintech evaluates functionality, cost, and
        admin controls; IT reviews cybersecurity and PII protection; Compliance reviews both and signs off that
        the due diligence meets regulatory standards. Every change is recorded in each record’s change history.
      </p>

      {error && <div style={{ ...card, borderColor: brand.red, color: brand.red }}>{error}</div>}

      {showCreate && (
        <div style={{ ...card, borderTop: `3px solid ${brand.goldenrod}` }}>
          <div style={{ ...label, marginBottom: '0.5rem' }}>New software record</div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input style={{ ...textInput, flex: '2 1 220px' }} placeholder="Software name" value={newName} onChange={(e) => setNewName(e.target.value)} />
            <input style={{ ...textInput, flex: '1 1 160px' }} placeholder="Category (optional)" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
            <Btn variant="primary" disabled={isCreating} onClick={handleCreate}>{isCreating ? 'Creating…' : 'Create'}</Btn>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 320px) 1fr', gap: '1.25rem', alignItems: 'start' }}>
        {/* List */}
        <div>
          {isLoading && <div style={card}>Loading…</div>}
          {!isLoading && software.length === 0 && <div style={{ ...card, color: brand.charcoal }}>No software vetted yet.</div>}
          {software.map(s => {
            const count = signoffCount(s);
            const active = s.id === selectedId;
            return (
              <button key={s.id} type="button" onClick={() => setSelectedId(s.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  background: active ? 'rgba(10,41,71,0.05)' : 'white',
                  border: `1px solid ${active ? brand.deepBlue : brand.softGray}`,
                  borderLeft: `4px solid ${count === 3 ? brand.green : brand.goldenrod}`,
                  borderRadius: 8, padding: '0.7rem 0.85rem', marginBottom: '0.6rem',
                }}>
                <div style={{ fontWeight: 700, color: brand.deepBlue }}>{s.name}</div>
                {s.category && <div style={{ color: brand.charcoal, fontSize: '0.78rem' }}>{s.category}</div>}
                <div style={{ marginTop: '0.35rem', fontSize: '0.72rem', fontWeight: 700, color: count === 3 ? brand.green : brand.charcoal }}>
                  <i className={`bi bi-${count === 3 ? 'check-circle-fill' : 'hourglass-split'} me-1`} />
                  {count}/3 signed off
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail */}
        <SoftwareDetail id={selectedId} onDeleted={() => { setSelectedId(null); refetch(); }} />
      </div>
    </div>
  );
}
